<template>
  <v-card :outlined="!embedded" :flat="embedded" :color="embedded ? 'transparent' : undefined" class="pending-invitations mb-4">
    <v-card-title class="py-3" :class="{ 'px-0 pt-0': embedded }">
      <span v-if="!embedded" class="text-subtitle-1 font-weight-medium mr-3">{{ $t('onboarding.members.pending.title') }}</span>
      <v-chip v-if="awaitingApproval > 0" small color="primary" dark class="mr-3">
        {{ $tc('onboarding.members.pending.to-approve', awaitingApproval, { count: awaitingApproval }) }}
      </v-chip>
      <span class="text-caption text--secondary">{{ $tc('onboarding.members.pending.in-progress', total, { count: total }) }}</span>
      <v-spacer />
      <v-btn v-if="canInvite" small color="primary" @click="$emit('create')">
        <v-icon left small>mdi-account-plus-outline</v-icon>{{ $t('onboarding.members.create-invitation') }}
      </v-btn>
    </v-card-title>
    <v-divider />

    <v-alert v-if="error" type="error" dense text class="ma-3">{{ error }}</v-alert>

    <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="10"
        :no-data-text="$t('onboarding.members.pending.none')"
        :item-class="rowClass"
        dense
        hide-default-footer
        class="pending-invitations__table"
    >
      <template #item.person="{ item }">
        <router-link :to="{ name: 'organizations.members.registration', params: { id: organizationId, registrationId: item.id } }" class="font-weight-medium">
          {{ displayName(item) }}
        </router-link>
        <div class="text-caption text--secondary">{{ item.email }}</div>
        <!-- Invitée par une commande d'équipement : la commande l'attend, à un clic -->
        <div v-if="orderIdOf(item)" class="text-caption">
          <v-icon x-small class="mr-1">{{ kindIcon(linkedOrders[orderIdOf(item)] && linkedOrders[orderIdOf(item)].kind) }}</v-icon>
          <router-link :to="orderRoute(orderIdOf(item))">{{ linkedOrderLabel(item) }}</router-link>
        </div>
      </template>
      <template #item.source="{ item }">
        {{ $t(`onboarding.registration.source.${item.source}`) }}
      </template>
      <template #item.status="{ item }">
        <span :class="{ 'error--text font-weight-medium': item.status === 'awaiting-approval' && !incompleteForApproval(item) }">
          {{ $t(`onboarding.registration.status.${item.status}`) }}
        </span>
        <span class="text-caption text--secondary"> · {{ item.createdAt | formatDate }}</span>
      </template>
      <template #item.action="{ item }">
        <template v-if="item.status === 'awaiting-approval' && !incompleteForApproval(item)">
          <template v-if="canApprove">
            <v-btn x-small outlined class="mr-2" :disabled="busy === item.id" @click="askRejection(item)">
              {{ $t('onboarding.registration.approval.reject') }}
            </v-btn>
            <v-btn x-small color="success" :loading="busy === item.id" @click="decide(item, true)">
              {{ $t('onboarding.registration.approval.approve') }}
            </v-btn>
          </template>
          <span v-else class="text-caption text--secondary">{{ $t('onboarding.members.pending.approver-needed') }}</span>
        </template>
        <!-- La création du compte s'est arrêtée : il y a quelque chose à corriger, puis à reprendre. -->
        <router-link v-else-if="item.provisioningFailed" :to="{ name: 'organizations.members.registration', params: { id: organizationId, registrationId: item.id } }" class="text-caption error--text font-weight-medium">
          {{ $t('onboarding.members.pending.provisioning-failed') }}
        </router-link>
        <template v-else-if="waitsForPerson(item)">
          <span class="text-caption text--secondary mr-1">{{ $t('onboarding.members.pending.waits-for-person') }}</span>
          <v-btn v-if="canInvite" x-small text color="primary" :loading="busy === item.id" @click="resend(item)">
            {{ $t('onboarding.members.pending.resend') }}
          </v-btn>
        </template>
        <span v-else class="text-caption text--secondary">{{ $t('onboarding.members.pending.nothing-to-do') }}</span>
        <registration-cancel-menu v-if="canInvite" small :registration-id="item.id" :name="displayName(item)" :order-id="orderIdOf(item)" @cancelled="load" />
      </template>
    </v-data-table>

    <v-dialog v-model="rejection.open" max-width="480">
      <v-card>
        <v-card-title class="text-h6">{{ $t('onboarding.registration.approval.reject') }} — {{ rejection.name }}</v-card-title>
        <v-card-text>
          <p class="text-caption">{{ $t('onboarding.registration.approval.reason-explained') }}</p>
          <v-textarea v-model="rejection.reason" :label="$t('onboarding.registration.approval.reason')" outlined rows="3" auto-grow />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="rejection.open = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="!rejection.reason" :loading="busy === rejection.id" @click="decide(rejection.item, false)">
            {{ $t('onboarding.registration.approval.reject') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Annuler une invitation qui n'a pas abouti : son lien dira « invitation annulée » -->
  </v-card>
</template>

<script>
import CommissioningRepository from '@repository/CommissioningRepository'
import RegistrationRepository from '@repository/RegistrationRepository'
import CommissioningMixin from '../CommissioningMixin'
import RegistrationCancelMenu from './RegistrationCancelMenu.vue'

/** La référence d'une invitation partie d'une commande d'équipement (passerelle commande → inscription). */
const ORDER_REFERENCE = 'equipment-order:'

/**
 * Les invitations en attente d'une organisation, dans son onglet Membres : ce qui attend le
 * gestionnaire (les demandes à valider, en tête) et ce qui attend la personne. On agit depuis la
 * ligne ; la fiche du dossier reste à un clic.
 */
export default {
  name: 'PendingInvitations',

  components: { RegistrationCancelMenu },

  mixins: [CommissioningMixin],

  props: {
    organizationId: { type: String, required: true },
    /** Dans la fenêtre d'un bouton : ni cadre ni titre (la fenêtre a le sien). */
    embedded: { type: Boolean, default: false },
  },

  data() {
    return {
      items: [],
      total: 0,
      awaitingApproval: 0,
      loading: false,
      error: null,
      busy: null,
      rejection: { open: false, item: null, id: null, name: '', reason: '' },
      /** Les commandes qui ont invité ces personnes : { id: { kind } }, lues à part. */
      linkedOrders: {},
    }
  },

  computed: {
    permissions() {
      return this.currentUser?.permissions || []
    },
    canInvite() {
      return this.permissions.includes('ONBOARDING_INVITE') || this.permissions.includes('ONBOARDING_MODULE_MANAGE')
    },
    canApprove() {
      return this.permissions.includes('MEMBER_APPROVE')
    },
    headers() {
      return [
        { text: this.$t('onboarding.registration.applicant'), value: 'person', sortable: false },
        { text: this.$t('onboarding.members.pending.came-by'), value: 'source', sortable: false },
        { text: this.$t('onboarding.members.pending.where'), value: 'status', sortable: false },
        { text: this.$t('onboarding.members.pending.expected-action'), value: 'action', sortable: false, align: 'end' },
      ]
    },
  },

  watch: {
    organizationId: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const data = await RegistrationRepository.list({ organizationId: this.organizationId, pending: true, offset: 0, limit: 100 })
        // Les demandes à valider d'abord : c'est la seule chose que le gestionnaire doit faire.
        this.items = [...data.items].sort((a, b) => (b.status === 'awaiting-approval') - (a.status === 'awaiting-approval'))
        this.total = data.total
        this.awaitingApproval = data.awaitingApproval || 0
        this.$emit('counts', { total: this.total, awaitingApproval: this.awaitingApproval })
        this.loadLinkedOrders()
      } catch (e) {
        this.error = e?.response?.data?.message || this.$t('error-occurred')
      } finally {
        this.loading = false
      }
    },

    orderIdOf(item) {
      const reference = item.businessReference || ''
      return reference.startsWith(ORDER_REFERENCE) ? reference.slice(ORDER_REFERENCE.length) : null
    },

    /** Ce que la commande liée commande (une borne, un compteur) : lu une fois par commande. */
    async loadLinkedOrders() {
      const ids = this.items.map(this.orderIdOf).filter(id => id && !this.linkedOrders[id])
      const orders = await Promise.all(ids.map(id => CommissioningRepository.get(id).catch(() => null)))
      const known = { ...this.linkedOrders }
      ids.forEach((id, i) => { known[id] = { kind: orders[i]?.kind || null } })
      this.linkedOrders = known
    },

    linkedOrderLabel(item) {
      const kind = this.linkedOrders[this.orderIdOf(item)]?.kind
      return kind
        ? this.$t('onboarding.members.pending.linked-order', { kind: this.kindLabel(kind).toLowerCase() })
        : this.$t('onboarding.members.pending.linked-order-unknown')
    },

    /** La commande, ouverte sans quitter la fiche de l'organisation. */
    orderRoute(id) {
      return { name: 'organizations.equipment-order', params: { id: this.organizationId, orderId: id } }
    },

    /** La personne a quelque chose à faire (compléter, payer) : on peut la relancer. */
    waitsForPerson(item) {
      return ['draft', 'invited', 'in-progress', 'awaiting-payment-method'].includes(item.status)
        || this.incompleteForApproval(item)
    },

    /** Devant l'approbation, mais la personne n'a pas fini sa part (informations, acompte à autoriser) : rien à décider encore. */
    incompleteForApproval(item) {
      return item.status === 'awaiting-approval' && item.completeForApproval === false
    },

    rowClass(item) {
      return item.status === 'awaiting-approval' ? 'pending-invitations__to-approve' : ''
    },

    displayName(item) {
      const name = [item.applicant?.firstname, item.applicant?.lastname].filter(Boolean).join(' ')
      return name || item.email
    },

    askRejection(item) {
      this.rejection = { open: true, item, id: item.id, name: this.displayName(item), reason: '' }
    },

    async decide(item, approved) {
      this.busy = item.id
      try {
        await RegistrationRepository.decideApproval(item.id, approved, approved ? null : this.rejection.reason)
        this.rejection.open = false
        this.notifySuccess(approved ? 'onboarding.members.pending.approved' : 'onboarding.members.pending.rejected')
        await this.load()
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.busy = null
      }
    },

    async resend(item) {
      this.busy = item.id
      try {
        await RegistrationRepository.sendInvitation(item.id)
        this.notifySuccess('onboarding.members.pending.resent')
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.busy = null
      }
    },
  },
}
</script>

<style scoped>
.pending-invitations__table >>> .pending-invitations__to-approve {
  background: #fff6f4;
}
</style>
